import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from '../components/Layout'
import CopyButton from '../components/CopyButton'

type CaseType = 'upper' | 'lower' | 'title' | 'sentence' | 'camel' | 'pascal' | 'snake' | 'kebab' | 'constant'

interface CaseOption {
  type: CaseType
  name: string
  icon: string
  transform: (text: string) => string
}

function TextCase() {
  const { t } = useTranslation()
  const [input, setInput] = useState('')
  const [selectedCase, setSelectedCase] = useState<CaseType>('upper')

  const caseOptions: CaseOption[] = [
    {
      type: 'upper',
      name: 'UPPER CASE',
      icon: 'keyboard_capslock',
      transform: (text) => text.toUpperCase()
    },
    {
      type: 'lower',
      name: 'lower case',
      icon: 'keyboard_arrow_down',
      transform: (text) => text.toLowerCase()
    },
    {
      type: 'title',
      name: 'Title Case',
      icon: 'title',
      transform: (text) => text.replace(/\w\S*/g, (txt) => 
        txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
      )
    },
    {
      type: 'sentence',
      name: 'Sentence case',
      icon: 'short_text',
      transform: (text) => {
        return text.toLowerCase().replace(/(^\s*\w|[.!?]\s*\w)/g, (c) => c.toUpperCase())
      }
    },
    {
      type: 'camel',
      name: 'camelCase',
      icon: 'code',
      transform: (text) => {
        return text
          .toLowerCase()
          .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
          .replace(/^[A-Z]/, (c) => c.toLowerCase())
      }
    },
    {
      type: 'pascal',
      name: 'PascalCase',
      icon: 'data_object',
      transform: (text) => {
        return text
          .toLowerCase()
          .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
          .replace(/^[a-z]/, (c) => c.toUpperCase())
      }
    },
    {
      type: 'snake',
      name: 'snake_case',
      icon: 'remove',
      transform: (text) => {
        return text
          .replace(/\s+/g, '_')
          .replace(/[A-Z]/g, (c) => '_' + c.toLowerCase())
          .replace(/[^a-zA-Z0-9_]/g, '_')
          .replace(/_+/g, '_')
          .replace(/^_|_$/g, '')
          .toLowerCase()
      }
    },
    {
      type: 'kebab',
      name: 'kebab-case',
      icon: 'horizontal_rule',
      transform: (text) => {
        return text
          .replace(/\s+/g, '-')
          .replace(/[A-Z]/g, (c) => '-' + c.toLowerCase())
          .replace(/[^a-zA-Z0-9-]/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '')
          .toLowerCase()
      }
    },
    {
      type: 'constant',
      name: 'CONSTANT_CASE',
      icon: 'tag',
      transform: (text) => {
        return text
          .replace(/\s+/g, '_')
          .replace(/[A-Z]/g, (c) => '_' + c)
          .replace(/[^a-zA-Z0-9_]/g, '_')
          .replace(/_+/g, '_')
          .replace(/^_|_$/g, '')
          .toUpperCase()
      }
    }
  ]

  const getOutput = () => {
    const option = caseOptions.find(o => o.type === selectedCase)
    return option ? option.transform(input) : input
  }

  const output = getOutput()

  const stats = {
    characters: input.length,
    words: input.trim() ? input.trim().split(/\s+/).length : 0,
    lines: input ? input.split('\n').length : 0
  }

  return (
    <Layout showBackButton title={t('toolPages.textCase.title')}>
      <div className="flex flex-col p-4 gap-6">
        {/* Input Section */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-end px-1">
            <label className="section-label">{t('toolPages.textCase.inputText')}</label>
            <button 
              onClick={() => setInput('')}
              className="text-xs font-medium text-slate-500 hover:text-primary transition-colors flex items-center gap-1"
            >
              <span className="material-symbols-outlined text-[16px]">backspace</span>
              {t('common.clear')}
            </button>
          </div>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="textarea"
            placeholder={t('toolPages.textCase.inputPlaceholder')}
            rows={5}
          />
          {/* Stats */}
          <div className="flex gap-4 text-xs text-slate-500 dark:text-slate-400 ml-1">
            <span>{stats.characters} {t('toolPages.textCase.characters')}</span>
            <span>{stats.words} {t('toolPages.textCase.words')}</span>
            <span>{stats.lines} {t('toolPages.textCase.lines')}</span>
          </div>
        </div>

        {/* Case Options */}
        <div className="flex flex-col gap-3">
          <label className="section-label">{t('toolPages.textCase.convertTo')}</label>
          <div className="grid grid-cols-3 gap-2">
            {caseOptions.map((option) => (
              <button
                key={option.type}
                onClick={() => setSelectedCase(option.type)}
                className={`flex flex-col items-center gap-1 p-3 rounded-xl border transition-all ${
                  selectedCase === option.type
                    ? 'bg-primary/10 border-primary text-primary'
                    : 'bg-white dark:bg-surface-dark border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:border-primary/50'
                }`}
              >
                <span className="material-symbols-outlined text-[20px]">{option.icon}</span>
                <span className="text-[10px] font-medium text-center leading-tight">{option.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Output Section */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-end px-1">
            <label className="section-label">{t('toolPages.textCase.output')}</label>
            <CopyButton text={output} />
          </div>
          <textarea
            value={output}
            readOnly
            className="textarea bg-slate-50 dark:bg-[#0d131a]"
            placeholder={t('toolPages.textCase.outputPlaceholder')}
            rows={5}
          />
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => setInput(output)}
            disabled={!output}
            className="flex-1 btn-secondary flex items-center justify-center gap-2"
          >
            <span className="material-symbols-outlined text-[18px]">swap_vert</span>
            {t('toolPages.textCase.useAsInput')}
          </button>
          <button
            onClick={async () => {
              try {
                const text = await navigator.clipboard.readText()
                setInput(text)
              } catch (err) {
                console.error('Failed to read clipboard')
              }
            }}
            className="flex-1 btn-secondary flex items-center justify-center gap-2"
          >
            <span className="material-symbols-outlined text-[18px]">content_paste</span>
            {t('toolPages.textCase.paste')}
          </button>
        </div>

        {/* Bottom spacing */}
        <div className="h-8"></div>
      </div>
    </Layout>
  )
}

export default TextCase
